import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import Delete from '@material-ui/icons/Delete';
import ConfirmationButton from 'components/shared/ConfirmationButton';
import moment from 'logic/moment';
import {
  MESSAGE_TYPE_TEXT,
  MESSAGE_TYPE_FILE,
  MESSAGE_TYPE_COMMUNICATION_WARNING,
  MESSAGE_TYPE_HOST_COMMUNICATION_WARNING,
} from 'constants/api';
import { blue, orange } from '@material-ui/core/colors';
import { Avatar, IconButton, Typography, makeStyles } from '@material-ui/core';
import LogoSimple from 'assets/img/logo-simple.png';
import Message from './Message';

const blacklist = [
  'filtered email',
  'filtered phone',
  '([a-z0-9._-]+@[a-z0-9_-]+.[a-z0-9]+|[0-9]{3}( |-)?[0-9]{2,3}( )?[0-9]{2,})',
  'bank account|bankkonto|IBAN|SMS|Swish|Paypal|Whatsapp|Messenger|Swedbank|Nordea|Handelsbanken',
];
const formatDate = (dateStr) => moment(dateStr).format('LLLL');
const getUserAvatar = (id, users) =>
  users
    .find((u) => u.id === id)
    .name.charAt(0)
    .toUpperCase();

const canDelete = (msg, currentUserId) =>
  [MESSAGE_TYPE_TEXT, MESSAGE_TYPE_FILE].includes(msg.type) &&
  !msg.deleted_at &&
  msg.user_id === currentUserId;

const getSeenUserName = (users = [], currentUserId) => {
  const user = users.filter(({ id }) => id !== currentUserId)[0];

  if (user && user.first_name) {
    return user.first_name;
  }
  if (user) {
    return user.name;
  }

  return '';
};

const styles = makeStyles((theme) => ({
  alignRight: {
    textAlign: 'right',
  },
  blueAvatar: {
    backgroundColor: blue[500],
    marginTop: theme.spacing(1),
  },
  msgWrap: {
    display: 'flex',
  },
  delete: {
    marginRight: -theme.spacing(1),
    marginTop: -theme.spacing(1),
    opacity: 0,
    pointerEvents: 'none',
    position: 'absolute',
    right: 0,
    transform: 'scale(0)',
    transition: 'opacity 150ms, transform 150ms',
  },
  msg: {
    margin: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    maxWidth: 640,
    position: 'relative',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',

    '& > p': {
      backgroundColor: blue[50],
      borderRadius: theme.spacing(1),
      padding: theme.spacing(1.6),
      marginBottom: theme.spacing(0.5),
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
    },

    '&:hover $delete': {
      opacity: 1,
      pointerEvents: 'auto',
      transform: 'scale(1)',
    },
  },
  warning: {
    '& > p': {
      backgroundColor: 'transparent',
      border: '2px solid #1ca5c5',
    },
  },
  deleted: {
    '& > p': {
      backgroundColor: theme.palette.grey[50],
    },
  },
  myMsg: {
    justifyContent: 'flex-end',
  },
  orangeAvatar: {
    backgroundColor: orange[500],
    marginTop: theme.spacing(1),
  },
  transparentAvatar: {
    backgroundColor: 'transparent',
    marginTop: theme.spacing(0.5),
  },
  seen: {
    backgroundColor: theme.palette.grey[50],
    borderRadius: 4,
    color: theme.palette.grey[700],
    margin: theme.spacing(1),
    padding: `0 ${theme.spacing(1)}px`,
  },
}));

const Caption = ({ msg, currentUserId, classes, data }) => {
  const intl = useIntl();
  let name = getSeenUserName(data.Users, currentUserId);

  if (name === 'Deleted') {
    name = intl.formatMessage({ id: 'Deleted', defaultMessage: 'Deleted' });
  }

  return (
    <Typography
      variant="caption"
      className={classNames(msg.user_id === currentUserId && classes.alignRight)}
    >
      {!!(msg.seen_by_users && msg.seen_by_users.filter((id) => id !== currentUserId).length) && (
        <span className={classes.seen}>
          <FormattedMessage
            id="Seen by"
            defaultMessage="Seen by {name}"
            values={{
              name,
            }}
          />
        </span>
      )}
      {msg.meta && msg.meta.cottage_id && msg.meta.source_url && (
        <>
          <FormattedMessage
            id="msg.msgFromMainSite"
            defaultMessage="This message was sent through the form on {link}"
            values={{
              link: <a href={msg.meta.source_url}>{msg.meta.source_url.replace('https://', '')}</a>,
            }}
          />
          <br />
        </>
      )}
      {formatDate(msg.created_at)}
    </Typography>
  );
};

Caption.propTypes = {
  msg: PropTypes.shape({
    user_id: PropTypes.number,
    seen_by_users: PropTypes.arrayOf(PropTypes.number),
    meta: PropTypes.shape({
      cottage_id: PropTypes.number,
      source_url: PropTypes.string,
    }),
    created_at: PropTypes.string,
    type: PropTypes.string,
  }),
  currentUserId: PropTypes.number,
  classes: PropTypes.shape({}),
  data: PropTypes.shape({
    Users: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

export const CommunicationWarning = ({ msg, currentUserId, classes }) => {
  if (msg.type !== MESSAGE_TYPE_TEXT) {
    return null;
  }

  if (!blacklist.some((bl) => new RegExp(bl, 'gi').test(msg.text))) {
    return null;
  }

  return (
    <div className={classNames(classes.msgWrap, msg.user_id === currentUserId && classes.myMsg)}>
      {msg.user_id !== currentUserId && (
        <Avatar className={classes.transparentAvatar}>
          <img src={LogoSimple} alt="Logo" />
        </Avatar>
      )}
      <div className={classNames(classes.msg, classes.warning)}>
        <Message
          msg={{
            conversation_id: msg.conversation_id,
            type: MESSAGE_TYPE_COMMUNICATION_WARNING,
          }}
          userName="Stugknuten"
          isMy={false}
        />
      </div>
      {msg.user_id === currentUserId && (
        <Avatar className={classes.transparentAvatar}>
          <img src={LogoSimple} alt="Logo" />
        </Avatar>
      )}
    </div>
  );
};

CommunicationWarning.propTypes = {
  msg: PropTypes.shape({
    type: PropTypes.string,
    user_id: PropTypes.number,
    conversation_id: PropTypes.number,
    text: PropTypes.string,
  }),
  currentUserId: PropTypes.number,
  classes: PropTypes.shape({}),
};

export const HostCommunicationWarning = ({ msg, classes, currentUserId }) => {
  if (msg.type !== MESSAGE_TYPE_TEXT) {
    return null;
  }

  if (!blacklist.some((bl) => new RegExp(bl, 'gi').test(msg.text))) {
    return null;
  }

  return (
    <div className={classNames(classes.msgWrap, msg.user_id === currentUserId && classes.myMsg)}>
      {msg.user_id !== currentUserId && (
        <Avatar className={classes.transparentAvatar}>
          <img src={LogoSimple} alt="Logo" />
        </Avatar>
      )}
      <div className={classNames(classes.msg, classes.warning)}>
        <Message
          msg={{
            conversation_id: msg.conversation_id,
            type: MESSAGE_TYPE_HOST_COMMUNICATION_WARNING,
          }}
          userName="Stugknuten"
          isMy={false}
        />
      </div>
      {msg.user_id === currentUserId && (
        <Avatar className={classes.transparentAvatar}>
          <img src={LogoSimple} alt="Logo" />
        </Avatar>
      )}
    </div>
  );
};

HostCommunicationWarning.propTypes = {
  msg: PropTypes.shape({
    type: PropTypes.string,
    user_id: PropTypes.number,
    conversation_id: PropTypes.number,
    text: PropTypes.string,
  }),
  currentUserId: PropTypes.number,
  classes: PropTypes.shape({}),
};

export const StugknutenMessage = ({ children }) => {
  const classes = styles();

  return (
    <div className={classNames(classes.msgWrap)}>
      <Avatar className={classes.transparentAvatar}>
        <img src={LogoSimple} alt="Logo" />
      </Avatar>
      <div className={classNames(classes.msg, classes.warning)}>
        <Typography component="p" variant="body2">
          {children}
        </Typography>
      </div>
    </div>
  );
};

StugknutenMessage.propTypes = {
  children: PropTypes.node,
};

const Messages = ({ currentUserId, data, deleteMessage, isHost }) => {
  const classes = styles();
  const intl = useIntl();

  return (
    <div>
      {data.Messages.map((msg) => {
        let userName = data.Users.find(({ id }) => id === msg.user_id).name;

        if (userName === 'Deleted') {
          userName = intl.formatMessage({ id: 'Deleted', defaultMessage: 'Deleted' });
        }

        return (
          <div key={msg.id}>
            <div
              className={classNames(
                classes.msgWrap,
                msg.user_id === currentUserId && classes.myMsg,
              )}
            >
              {msg.user_id !== currentUserId && (
                <Avatar className={classes.orangeAvatar}>
                  {getUserAvatar(msg.user_id, data.Users)}
                </Avatar>
              )}
              <div className={classNames(classes.msg, !!msg.deleted_at && classes.deleted)}>
                {canDelete(msg, currentUserId) && (
                  <ConfirmationButton
                    Component={IconButton}
                    aria-label="Close"
                    size="small"
                    className={classes.delete}
                    dialogText={
                      <FormattedMessage id="Are you sure?" defaultMessage="Are you sure?" />
                    }
                    onConfirm={() => deleteMessage({ conversationId: data.id, messageId: msg.id })}
                  >
                    <Delete fontSize="small" />
                  </ConfirmationButton>
                )}
                <Message msg={msg} userName={userName} isMy={msg.user_id === currentUserId} />
                <Caption
                  {...{
                    msg,
                    data,
                    currentUserId,
                    classes,
                  }}
                />
              </div>
              {msg.user_id === currentUserId && (
                <Avatar className={classes.blueAvatar}>
                  {getUserAvatar(msg.user_id, data.Users)}
                </Avatar>
              )}
            </div>
            {data?.filters?.commission &&
              data?.filters?.conversation_bookings === 0 &&
              data?.filters?.total_host_bookings_18m < 2 &&
              isHost && <HostCommunicationWarning {...{ msg, currentUserId, classes }} />}
            {data?.filters?.commission && !isHost && (
              <CommunicationWarning {...{ msg, currentUserId, classes }} />
            )}
          </div>
        );
      })}
    </div>
  );
};

Messages.propTypes = {
  currentUserId: PropTypes.number.isRequired,
  data: PropTypes.shape({
    Messages: PropTypes.arrayOf(PropTypes.shape({})),
    Users: PropTypes.arrayOf(PropTypes.shape({})),
    id: PropTypes.number,
    filters: PropTypes.shape({
      emails: PropTypes.bool,
      phone_numbers: PropTypes.bool,
      files: PropTypes.bool,
      links: PropTypes.bool,
      commission: PropTypes.bool,
      bank_information: PropTypes.bool,
      conversation_bookings: PropTypes.number,
      total_host_bookings_18m: PropTypes.number,
      termination_warning: PropTypes.bool,
    }),
  }).isRequired,
  deleteMessage: PropTypes.func.isRequired,
  isHost: PropTypes.bool.isRequired,
};

export default Messages;
